import { v4 as uuidv4 } from "uuid";
import API from "../../services/API";
import baseElementTemplate from "../../data/baseElement.json";
import { useContent } from "../../context/ContentContext";
import {
  getElementFileConfig,
  getElementUploadContentType,
  isElementImageFile,
} from "../../utility/elementFiles";

const cloneBaseElementTemplate = () => JSON.parse(JSON.stringify(baseElementTemplate));

const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = (err) => reject(err);
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });

const mergeInitialElementData = (basePayload, initialElementData) =>
  initialElementData
    ? {
        ...basePayload,
        ...initialElementData,
        labelData: {
          ...basePayload.labelData,
          ...initialElementData.labelData,
        },
        visualizerData: {
          ...basePayload.visualizerData,
          ...initialElementData.visualizerData,
        },
        worldUnit: {
          ...basePayload.worldUnit,
          ...initialElementData.worldUnit,
        },
      }
    : basePayload;

export const useElementUploadWorkflow = ({
  initialElementId,
  initialElementData,
} = {}) => {
  const { upsertMyElement, patchMyElement, trackElementBuild } = useContent();

  const createElementPayload = async ({ file, name, description, optimization }) => {
    const basePayload = cloneBaseElementTemplate();
    const newElementPayload = mergeInitialElementData(basePayload, initialElementData);

    const elementId = initialElementId || newElementPayload.id || uuidv4();
    newElementPayload.id = newElementPayload.dataObjectID = elementId;

    if (name && name.trim()) {
      newElementPayload.labelData.displayName = name.trim();
    }
    if (description && description.trim()) {
      newElementPayload.labelData.description = description.trim();
    }
    newElementPayload.fileName = file.name;
    if (optimization) {
      newElementPayload.optimization = optimization;
    }

    const fileConfig = getElementFileConfig(file.name);
    if (fileConfig?.visualizerData) {
      Object.assign(newElementPayload.visualizerData, fileConfig.visualizerData);
    }
    if (fileConfig?.worldUnit) {
      Object.assign(newElementPayload.worldUnit, fileConfig.worldUnit);
    }

    if (isElementImageFile(file.name)) {
      try {
        const { width, height } = await getImageDimensions(file);
        if (width > 0) {
          newElementPayload.visualizerData.xValue = height / width;
        }
      } catch (err) {
        console.error("Failed to get image dimensions:", err);
      }
    }

    return newElementPayload;
  };

  const uploadSingleElement = async ({ file, name, description, optimization }) => {
    const newElementPayload = await createElementPayload({
      file,
      name,
      description,
      optimization,
    });
    const createdElement = {
      id: newElementPayload.id,
      name: newElementPayload.labelData.displayName,
      description: newElementPayload.labelData.description,
      dateModified: new Date().toISOString(),
      status: {
        type: "BUILDING",
        timestamp: new Date().toISOString(),
      },
    };

    try {
      const response = await API.post("elements/", newElementPayload, {
        requiresAuth: true,
      });
      const { sourceUploadUrl } = response.data.data;

      upsertMyElement(createdElement, { prepend: true });

      if (file && sourceUploadUrl) {
        const contentType = getElementUploadContentType(file);
        const uploadHeaders = contentType ? { "Content-Type": contentType } : undefined;
        await fetch(sourceUploadUrl, {
          method: "PUT",
          body: file,
          headers: uploadHeaders,
        });
      }

      await API.post("build-element", newElementPayload, {
        requiresAuth: true,
      });
      trackElementBuild(createdElement.id);

      return createdElement;
    } catch (error) {
      patchMyElement(createdElement.id, {
        status: {
          type: "BUILDING_FAILED",
          timestamp: new Date().toISOString(),
          message: error?.message || "Upload failed",
        },
      });
      throw error;
    }
  };

  return {
    createElementPayload,
    uploadSingleElement,
  };
};

export default useElementUploadWorkflow;
